/**
 * Author: Fleming Shi
 * Project: DAL (Digital Abstraction Layer)
 * Date: 2025-11-08
 * Revision: 1.0
 * Description: Authentication context provider for managing user auth state
 */

import React, { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { loginRequest } from '@config/authConfig'
import { API_BASE_URL } from '@services/api'
import { getMsalInstance } from '@services/authService'

interface AuthContextType {
  isAuthenticated: boolean
  user: any | null
  login: () => Promise<void>
  logout: () => Promise<void>
  isLoading: boolean
  authError: string | null
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

/** Pull a user-facing message off a failed auth response. */
async function readAuthError(res: Response): Promise<string> {
  try {
    const body = await res.json()
    // Only coded errors carry a message meant for the user; everything else
    // (401s, proxy errors) gets the generic text.
    if (body?.code && typeof body.error === 'string') {
      return body.error
    }
  } catch {
    // non-JSON error response — fall through to the generic message
  }
  return 'Sign-in failed. Please try again.'
}

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}

interface AuthProviderProps {
  children: ReactNode
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [user, setUser] = useState<any | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [authError, setAuthError] = useState<string | null>(null)
  const initStarted = useRef(false)
  const navigate = useNavigate()

  useEffect(() => {
    // Guard against React 18 StrictMode double-invocation in development
    if (initStarted.current) return
    initStarted.current = true
    initializeAuth()
  }, [])

  const initializeAuth = async () => {
    try {
      const msalInstance = await getMsalInstance()

      // Handle the post-login redirect from Entra ID
      try {
        const redirectResponse = await msalInstance.handleRedirectPromise()
        console.log('[Auth] handleRedirectPromise result:', redirectResponse ? 'got response' : 'no redirect')

        if (redirectResponse?.account) {
          console.log('[Auth] Redirect login succeeded for:', redirectResponse.account.username)

          // Exchange MSAL token for httpOnly session cookie
          const sessionRes = await fetch(`${API_BASE_URL}/auth/session`, {
            method: 'POST',
            credentials: 'include',
            headers: { Authorization: `Bearer ${redirectResponse.accessToken}` },
          })

          // Fully clear MSAL cache — token must not remain in browser-accessible
          // storage, including when the exchange below is rejected.
          await msalInstance.clearCache()

          if (!sessionRes.ok) {
            const message = await readAuthError(sessionRes)
            console.error(`[Auth] Session exchange failed: ${sessionRes.status} — ${message}`)
            setAuthError(message)
            setIsLoading(false)
            return
          }

          // Load user identity from the newly established session
          const meRes = await fetch(`${API_BASE_URL}/auth/me`, { credentials: 'include' })
          if (meRes.ok) {
            const body = await meRes.json()
            setUser(body.data ?? null)
            setIsAuthenticated(true)
            navigate('/', { replace: true })
          } else {
            const message = await readAuthError(meRes)
            console.error(`[Auth] /me failed immediately after exchange: ${meRes.status} — ${message}`)
            setAuthError(message)
          }

          setIsLoading(false)
          return
        }
      } catch (error) {
        console.error('[Auth] Error during redirect handling or session exchange:', error)
        setIsLoading(false)
        return
      }

      // Page load / refresh — check whether a server-side session already exists.
      // The session cookie is sent automatically; no MSAL account check needed.
      try {
        const meRes = await fetch(`${API_BASE_URL}/auth/me`, { credentials: 'include' })
        if (meRes.ok) {
          const body = await meRes.json()
          setUser(body.data ?? null)
          setIsAuthenticated(true)
        } else if (meRes.status === 503) {
          setAuthError(await readAuthError(meRes))
        }
      } catch {
        // network error — stay unauthenticated
      }
    } catch (error) {
      console.error('[Auth] Auth initialization failed:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const login = async () => {
    try {
      setIsLoading(true)
      setAuthError(null)
      const msalInstance = await getMsalInstance()

      // Use redirect flow (not popup)
      await msalInstance.loginRedirect(loginRequest)
      // Will never reach here — page navigates away to Entra
    } catch (error: any) {
      console.error('[Auth] Login failed:', error?.errorCode, error?.message)

      // Clear stale MSAL interaction state so the next attempt works
      if (error?.errorCode === 'interaction_in_progress') {
        console.warn('[Auth] Clearing stale interaction state')
        sessionStorage.clear()
      }

      setIsLoading(false)
      throw error
    }
  }

  const logout = async () => {
    try {
      setIsLoading(true)

      // Destroy server-side session first
      try {
        await fetch(`${API_BASE_URL}/auth/logout`, { method: 'POST', credentials: 'include' })
      } catch (err) {
        console.error('[Auth] Backend logout failed:', err)
      }

      // Clear local auth state
      setUser(null)
      setIsAuthenticated(false)
      setAuthError(null)

      // Clear any remaining MSAL state (should already be empty, but be safe)
      try {
        const msalInstance = await getMsalInstance()
        await msalInstance.clearCache()
        sessionStorage.clear()
      } catch {
        // ignore
      }
    } catch (error) {
      console.error('[Auth] Logout failed:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const value = {
    isAuthenticated,
    user,
    login,
    logout,
    isLoading,
    authError,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
